import React from 'react';
import { Flex, Box, Text, HStack, Button, Icon, Badge, Link } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiUserCheck, FiUsers, FiArrowRight } from 'react-icons/fi';
import Card from 'components/card/Card';
import useAppToast from 'components/toast';
import UserPicker, { userLabel } from 'components/userPicker/UserPicker';
import { useSelector } from 'react-redux';
import { useListAssignableUsersQuery, useAssignUserMutation } from 'api/metaWhatsappApi';

/**
 * The step between connecting a number and proving it works.
 *
 * ★ A CONVENIENCE, NOT THE PLACE ASSIGNMENT LIVES.
 * A number with nobody on it is connectable and unusable — it cannot send, and
 * every conversation on it is invisible to every human — so this is the natural
 * moment to put someone on it. It is still SKIPPABLE, because the connection is
 * genuinely complete without it and blocking a wizard on a decision that can be
 * changed weekly would be the wrong trade. The permanent home is the
 * Assignments tab, and the link below says so rather than leaving people to
 * discover it.
 *
 * ★ "ASSIGN TO MYSELF" IS THE COMMON CASE AND DESERVES ONE CLICK.
 * The administrator setting a number up is usually the person who will test it,
 * and the outbound test in the next step needs the caller to be assigned — not
 * merely someone, but them. Making that a search-and-select would be three
 * interactions to reach the obvious answer.
 */

const ASSIGNMENTS_TAB = '/whatsapp/settings?tab=assignments';

export default function InitialAssignment({ channel, onSkip }) {
	const notify = useAppToast();
	const phoneNumberId = channel?.phoneNumberId;
	const [assignUser, { isLoading }] = useAssignUserMutation();

	// ★ state.user.user is the app's canonical signed-in user — localSlice
	// hydrates it from storage at boot and every other screen reads it there.
	const me = useSelector((state) => state.user?.user ?? null);
	const myId = me?._id ?? me?.userId ?? null;

	const useSearchPeople = (q, page, isOpen) => {
		const { data, isFetching } = useListAssignableUsersQuery(
			{ phoneNumberId, q, page },
			{ skip: !isOpen || !phoneNumberId },
		);
		return { users: data?.users ?? [], pagination: data?.pagination ?? null, isFetching };
	};

	const assign = async (userId, label) => {
		try {
			await assignUser({ phoneNumberId, userId }).unwrap();
			notify.success('Assigned', `${label} can now use this number.`);
		} catch (err) {
			notify.error('Not assigned', err?.data?.reason ?? 'That assignment could not be saved.');
		}
	};

	return (
		<Card bg="bg.surface" borderWidth="1px" borderColor="border.default">
			<HStack justify="space-between" align="start" mb="1" spacing="3">
				<HStack spacing="2">
					<Badge colorScheme="gray" variant="subtle" fontSize="0.55rem">
						Next
					</Badge>
					<Text fontSize="sm" fontWeight="bold" color="text.primary">
						Give someone this number
					</Text>
				</HStack>
				{onSkip && (
					<Button size="xs" variant="ghost" onClick={onSkip}>
						Skip for now
					</Button>
				)}
			</HStack>

			<Text fontSize="xs" color="text.muted" mb="3" lineHeight="1.7">
				Only assigned people see this number&apos;s conversations or can send from it. Until someone
				is on it, messages arrive and nobody can read them.
			</Text>

			<Flex align="center" flexWrap="wrap" gap="2" mb="3">
				<Button
					size="sm"
					leftIcon={<Icon as={FiUserCheck} />}
					onClick={() => assign(myId, 'You')}
					isLoading={isLoading}
					isDisabled={!myId}
				>
					Assign to myself
				</Button>
				{/* The test in the next step sends as the CALLER, so assigning
				    someone else leaves this administrator unable to run it. Said
				    here rather than discovered as a refusal two steps later. */}
				<Text fontSize="0.7rem" color="text.muted">
					or add someone else below
				</Text>
			</Flex>

			<UserPicker
				useSearch={useSearchPeople}
				onSelect={(user) => assign(user._id, userLabel(user))}
				isBusy={isLoading}
				placeholder="Search people by name or email…"
				exhaustedText="Everyone is already assigned."
				renderMeta={(u) =>
					u.canUseInbox === false ? (
						<Badge colorScheme="yellow" variant="subtle" fontSize="0.55rem">
							No inbox access
						</Badge>
					) : null
				}
				renderHint={(u) =>
					u.canUseInbox === false ? 'Needs the WhatsApp module on their role' : u.email
				}
			/>

			<HStack spacing="1.5" mt="3">
				<Icon as={FiUsers} color="text.muted" boxSize="3" />
				<Text fontSize="0.7rem" color="text.muted">
					You can change this any time in{' '}
					<Link as={RouterLink} to={ASSIGNMENTS_TAB} color="text.accent" textDecoration="underline">
						Assignments
					</Link>{' '}
					— it never touches the Meta connection.
				</Text>
				<Icon as={FiArrowRight} color="text.muted" boxSize="3" />
			</HStack>
		</Card>
	);
}
